import { RoleService } from './role/role.service';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { UserRole } from '../services/auth/auth.role';
import { AuthService } from '../services/auth/auth.service';
import { NavigationItem, DropdownItem } from 'htx-ui-library';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

interface NavigationRoute extends NavigationItem {
  visibleForRoles?: UserRole[];
  visibleInExtraMenu?: boolean;
}

// Role constants for routes
export const PARTNERNETWORK_ROLES: UserRole[] = [UserRole.OrganizationUser];

export const ADMIN_ROLES: UserRole[] = [UserRole.OrganizationAdmin,
  UserRole.OrganizationRepresentative, UserRole.DataspaceAuthority];

export const DATASPACE_AUTHORITY_ROLES: UserRole[] = [UserRole.DataspaceAuthority];

export const ORGANIZATION_EDIT_ROLES: UserRole[] = [
  UserRole.OrganizationAdmin,
  UserRole.OrganizationRepresentative,
  UserRole.DataspaceAuthority
];

export const MEMBER_EDIT_ROLES: UserRole[] = [
  UserRole.OrganizationAdmin,
  UserRole.OrganizationRepresentative,
  UserRole.DataspaceAuthority
];

export const MEMBER_ADD_ROLES: UserRole[] = [
  UserRole.OrganizationAdmin,
  UserRole.OrganizationRepresentative,
  UserRole.DataspaceAuthority
];

@Injectable({ providedIn: 'root' })
export class NavigationService {
  private router = inject(Router);
  private roleService = inject(RoleService);
  private authService = inject(AuthService);
  private transloco = inject(TranslocoService);

  private routes: NavigationRoute[] = [
    {
      label: 'app.title',
      icon: 'home',
      onItemClick: () => {
        this.router.navigate(['/']);
      }
    },
    {
      label: 'menu.participate',
      icon: 'handshake',
      onItemClick: () => {}
    },
    {
      label: 'menu.explore',
      icon: 'explore',
      onItemClick: () => {}
    },
    {
      label: 'menu.usecases',
      icon: 'lightbulb',
      dropdownItems: [
        {
          label: 'usecases.supplyChain.title',
          icon: 'account_tree',
          value: {
            onItemClick: () => this.router.navigate(['/use-cases/supply-chain'])
          }
        },
        {
          label: 'usecases.shortageManagement.title',
          icon: 'show_chart',
          value: {
            onItemClick: () => this.router.navigate(['/use-cases/shortage-management'])
          }
        }
      ]
    },
    {
      label: 'menu.partnernetwork',
      icon: 'group',
      onItemClick: () => {
        this.router.navigate(['/partnernetwork']);
      },
      visibleForRoles: PARTNERNETWORK_ROLES
    },
    {
      label: 'menu.admin.dashboard',
      icon: 'dashboard',
      onItemClick: () => {
        this.router.navigate(['/admin/dashboard']);
      },
      visibleForRoles: ADMIN_ROLES,
      visibleInExtraMenu: true
    },
    {
      label: 'menu.admin.requests',
      icon: 'description',
      onItemClick: () => {
        this.router.navigate(['/admin/requests']);
      },
      visibleForRoles: DATASPACE_AUTHORITY_ROLES,
      visibleInExtraMenu: true
    },
    {
      label: 'menu.admin.organizations',
      icon: 'business',
      onItemClick: () => {
        this.router.navigate(['/admin/organizations']);
      },
      visibleForRoles: ADMIN_ROLES,
      visibleInExtraMenu: true
    }
  ];

  getNavigationRoutes$(): Observable<NavigationItem[]> {
    return this.roleService.getUserRoles$().pipe(
      switchMap((userRoles) => {
        const keys = this.routes
          .filter((route) => {
            if (route.visibleInExtraMenu) return false;
            if (route.visibleForRoles) {
              return route.visibleForRoles.some((role) => userRoles.includes(role));
            }
            return true;
          })
          .map((route) => route.label);

        return this.transloco.selectTranslate(keys).pipe(
          map((translations) => {
            const filtered = this.routes.filter((route) => {
              if (route.visibleInExtraMenu) return false;
              if (route.visibleForRoles) {
                return route.visibleForRoles.some((role) => userRoles.includes(role));
              }
              return true;
            });

            return filtered.map((route) => ({
              ...route,
              label: this.transloco.translate(route.label),
              dropdownItems: route.dropdownItems?.map((item) => ({
                ...item,
                label: this.transloco.translate(item.label)
              }))
            }));
          })
        );
      })
    );
  }

  getAdminMenuRoutes$(): Observable<DropdownItem[]> {
    return this.roleService.getUserRoles$().pipe(
      switchMap((userRoles) => {
        if (
          !userRoles.includes(UserRole.OrganizationAdmin) &&
          !userRoles.includes(UserRole.DataspaceAuthority)
        )
          return of([]);

        const keys = this.routes
          .filter(
            (route) =>
              route.visibleInExtraMenu &&
              route.visibleForRoles?.some((role) => userRoles.includes(role))
          )
          .map((route) => route.label);

        return this.transloco.selectTranslate(keys).pipe(
          map((translations) => {
            const filtered = this.routes.filter(
              (route) =>
                route.visibleInExtraMenu &&
                route.visibleForRoles?.some((role) => userRoles.includes(role))
            );
            return filtered.map((route) => {
              let label = this.transloco.translate(route.label);

              // For organizations route: show 'menu.admin.organization' if only OrganizationAdmin (not DataspaceAuthority)
              if (
                route.label === 'menu.admin.organizations' &&
                userRoles.includes(UserRole.OrganizationAdmin) &&
                !userRoles.includes(UserRole.DataspaceAuthority)
              ) {
                label = this.transloco.translate('menu.admin.organization');
              }

              return {
                ...route,
                label,
                icon: (route as any)['icon'] ?? '',
                value: this.extractRoutePath(route)
              };
            });
          })
        );
      })
    );
  }

  getProfileMenuRoutes$(): Observable<DropdownItem[]> {
    return this.roleService.getUserRoles$().pipe(
      switchMap((userRoles) => {
        const isAdmin =
          userRoles.includes(UserRole.OrganizationAdmin) ||
          userRoles.includes(UserRole.OrganizationRepresentative) ||
          userRoles.includes(UserRole.DataspaceAuthority);

        const adminRoutes = isAdmin
          ? this.routes.filter(
              (route) =>
                route.visibleInExtraMenu &&
                route.visibleForRoles?.some((role) => userRoles.includes(role))
            )
          : [];

        const keysToTranslate = [
          'menu.profile',
          ...adminRoutes.map((r) => r.label),
          ...(isAdmin ? ['menu.admin.organization'] : []),
          'menu.logout'
        ];

        return this.transloco.selectTranslate(keysToTranslate).pipe(
          map(() => {
            const profileItem = {
              label: this.transloco.translate('menu.profile'),
              icon: 'manage_accounts',
              value: '/profile',
              onItemClick: () => this.router.navigate(['/profile'])
            };

            const adminItems: DropdownItem[] = adminRoutes.map((route) => {
              let label = this.transloco.translate(route.label);
              if (
                route.label === 'menu.admin.organizations' &&
                (userRoles.includes(UserRole.OrganizationAdmin) || userRoles.includes(UserRole.OrganizationRepresentative)) &&
                !userRoles.includes(UserRole.DataspaceAuthority)
              ) {
                label = this.transloco.translate('menu.admin.organization');
              }
              return {
                ...route,
                label,
                icon: (route as any)['icon'] ?? '',
                value: this.extractRoutePath(route)
              };
            });

            // Logout-Eintrag ergänzen
            const logoutItem = {
              label: this.transloco.translate('menu.logout'),
              icon: 'logout',
              value: '__logout__',
              onItemClick: () => this.authService.logout()
            };

            return [profileItem as unknown as DropdownItem, ...adminItems, logoutItem];
          })
        );
      })
    );
  }

  /**
   * Redirects logged-in users to the home page.
   * Used to prevent authenticated users from accessing public-only pages like registration.
   * @returns true if the user was redirected, false otherwise
   */
  redirectIfLoggedIn(): boolean {
    if (this.authService.isLoggedIn$.value) {
      void this.router.navigate(['/home']);
      return true;
    }
    return false;
  }

  /**
   * Get required roles for partnernetwork route
   */
  getAccessRoles(route: string): UserRole[] {
    if (route === 'partnernetwork') {
      return PARTNERNETWORK_ROLES;
    }
    if (route === 'admin/dashboard' || route === 'admin/organizations') {
      return ADMIN_ROLES;
    }
    if (route === 'admin/requests') {
      return DATASPACE_AUTHORITY_ROLES;
    }
    if(route === 'admin/organizations/edit') {
      return ORGANIZATION_EDIT_ROLES;
    }
    if(route === 'admin/organizations/members/edit') {
      return MEMBER_EDIT_ROLES;
    }
    if(route === 'admin/organizations/members/add') {
      return MEMBER_ADD_ROLES;
    }
    return [];
  }

  /**
   * Helper to extract the route path from a NavigationRoute's onItemClick navigation.
   * Assumes the route is navigated via router.navigate([path]) in onItemClick.
   * If not available, returns an empty string.
   */
  private extractRoutePath(route: NavigationRoute): string {
    // This is a placeholder. In a real implementation, store the path as a property on the route.
    // For now, try to infer from the navigation function string (not robust, but works for this pattern).
    if (route.onItemClick) {
      const fnStr = route.onItemClick.toString();
      const match = fnStr.match(/navigate\(\['"]([^'\"]+)['"]\)/);
      if (match && match[1]) {
        return match[1];
      }
    }
    return '';
  }
}
